import { addHours, eachDayOfInterval, eachWeekOfInterval, format, startOfHour } from 'date-fns';

/**
 * The periods of the Performances page and what the server is asked for
 * (W4). The figures are computed by the server (POST /api/analytics/dialer/*);
 * this file only turns the screen's choices into dates, picks the grain of
 * the trend chart, and rebuilds the server's bucket keys so an empty day
 * still shows as an empty bar.
 */

export const PERIODS = ['today', 'last_7_days', 'last_14_days', 'last_30_days', 'last_6_months', 'last_12_months', 'custom'] as const;
export type Period = (typeof PERIODS)[number];

export const COMPARE_MODES = ['none', 'previous', 'prev_month', 'prev_year', 'custom'] as const;
export type CompareMode = (typeof COMPARE_MODES)[number];

export interface Bounds { start: Date | null; end: Date | null }

const DAY_MS = 86_400_000;

/** The dates of a period; « today » runs to the end of the day (a call stamped a few seconds ahead stays in). */
export function resolvePeriod(period: string, customStart = '', customEnd = '', now = new Date()): Bounds {
  const daysBack = (n: number) => { const s = new Date(now); s.setDate(s.getDate() - n); s.setHours(0, 0, 0, 0); return s; };
  const monthsBack = (n: number) => { const s = new Date(now); s.setMonth(s.getMonth() - n); s.setHours(0, 0, 0, 0); return s; };
  switch (period) {
    case 'today': {
      const s = new Date(now); s.setHours(0, 0, 0, 0);
      const e = new Date(now); e.setHours(23, 59, 59, 999);
      return { start: s, end: e };
    }
    case 'last_7_days': return { start: daysBack(7), end: now };
    case 'last_14_days': return { start: daysBack(14), end: now };
    case 'last_30_days': return { start: daysBack(30), end: now };
    case 'last_6_months': return { start: monthsBack(6), end: now };
    case 'last_12_months': return { start: monthsBack(12), end: now };
    case 'custom': return {
      start: customStart ? new Date(`${customStart}T00:00:00`) : null,
      end: customEnd ? new Date(`${customEnd}T23:59:59.999`) : null,
    };
    default: return { start: null, end: null };
  }
}

/**
 * The comparison window (#14): the same length as the main period and never
 * overlapping it, or two custom dates. Null bounds: nothing to compare.
 */
export function compareBounds(mode: CompareMode, main: Bounds, customStart = '', customEnd = ''): Bounds {
  if (mode === 'none') return { start: null, end: null };
  if (mode === 'custom') return resolvePeriod('custom', customStart, customEnd);
  if (!main.start || !main.end) return { start: null, end: null };
  if (mode === 'previous') {
    const length = main.end.getTime() - main.start.getTime();
    return { start: new Date(main.start.getTime() - length), end: new Date(main.start.getTime() - 1) };
  }
  const shift = (months: number, years: number) => {
    const start = new Date(main.start!); const end = new Date(main.end!);
    start.setMonth(start.getMonth() - months); start.setFullYear(start.getFullYear() - years);
    end.setMonth(end.getMonth() - months); end.setFullYear(end.getFullYear() - years);
    return { start, end };
  };
  return mode === 'prev_month' ? shift(1, 0) : shift(0, 1);
}

export type Grain = 'hour' | 'day' | 'week';

/** Hours for a single day, days up to a month, weeks beyond: the bars stay readable. */
export function grainFor(b: Bounds): Grain {
  if (!b.start || !b.end) return 'week';
  const span = b.end.getTime() - b.start.getTime();
  return span <= 1.5 * DAY_MS ? 'hour' : span <= 31 * DAY_MS ? 'day' : 'week';
}

/** The server's bucket key of a date (GRAIN_FORMAT in server/src/application/analytics/dialerPipelines.ts). */
export function bucketKey(d: Date, grain: Grain): string {
  if (grain === 'hour') return format(d, "yyyy-MM-dd'T'HH");
  if (grain === 'day') return format(d, 'yyyy-MM-dd');
  return format(d, "RRRR-'W'II");
}

/**
 * Every bucket of the period, oldest first. For one day, only the hours from
 * the first to the last call (a 24-bar chart with 14 empty bars says nothing).
 */
export function bucketStarts(b: Bounds, grain: Grain, firstCall?: Date | null, lastCall?: Date | null): Date[] {
  const start = b.start ?? firstCall ?? null;
  const end = b.end ?? lastCall ?? null;
  if (!start || !end || end < start) return [];
  if (grain === 'hour') {
    const from = startOfHour(firstCall && firstCall > start ? firstCall : start);
    const to = startOfHour(lastCall && lastCall < end ? lastCall : end);
    const n = Math.max(0, Math.round((to.getTime() - from.getTime()) / 3_600_000)) + 1;
    return Array.from({ length: Math.min(n, 48) }, (_, i) => addHours(from, i));
  }
  if (grain === 'day') return eachDayOfInterval({ start, end });
  return eachWeekOfInterval({ start, end }, { weekStartsOn: 1 });
}

/** The browser's IANA time zone: days and hours are counted where the user is. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';
  } catch {
    return 'Europe/Paris';
  }
}

/** Number of days a period covers, for « appels / jour ». */
export function periodDays(b: Bounds): number | null {
  if (!b.start || !b.end) return null;
  return Math.max(1, Math.round((b.end.getTime() - b.start.getTime()) / DAY_MS));
}

const HUMAN_VERDICTS = ['human', 'human_jev', 'human_swoft', 'call_screening'];
const NOT_A_CONVERSATION = ['voicemail', 'left_voicemail', 'no_answer', 'not_reachable', 'failed', 'canceled', 'interrupted'];

/**
 * « Connected » as a query on the stored calls, the same rule as the server's
 * PARALLEL_CONNECTED (metricContracts.ts): agent_connected_at, and for calls
 * written before that field, a human verdict, a bridge to the agent, or an
 * answer that was not a machine. The drill-down lists exactly what the tile counts.
 */
export const CONNECTED_FILTER = {
  $or: [
    { agent_connected_at: { $ne: null } },
    {
      agent_connected_at: { $exists: false },
      $or: [
        { call_status: 'connected_to_agent' },
        { amd_result: { $in: HUMAN_VERDICTS } },
        { agent_bridged_at: { $ne: null } },
        { connected_at: { $ne: null }, call_outcome: { $nin: NOT_A_CONVERSATION } },
      ],
    },
  ],
};

export type DrillMetric = 'total' | 'connected' | 'conversations' | 'meetings';

/** The page-route filter of the calls behind a tile. */
export function drillFilter(metric: DrillMetric, o: { bounds: Bounds; agentEmail?: string; phones?: string[]; threshold: number }): Record<string, unknown> {
  const and: Array<Record<string, unknown>> = [];
  const range: Record<string, string> = {};
  if (o.bounds.start) range.$gte = o.bounds.start.toISOString();
  if (o.bounds.end) range.$lte = o.bounds.end.toISOString();
  if (Object.keys(range).length) and.push({ $or: [{ started_at: range }, { started_at: null, created_date: range }] });
  if (o.agentEmail) and.push({ agent_email: o.agentEmail });
  if (o.phones) and.push({ prospect_phone: { $in: o.phones } });
  if (metric === 'connected' || metric === 'conversations') and.push(CONNECTED_FILTER);
  if (metric === 'conversations') and.push({ duration_seconds: { $gte: o.threshold } });
  if (metric === 'meetings') and.push({ $or: [{ meeting_booked: true }, { call_outcome: 'meeting_booked' }] });
  return and.length ? { $and: and } : {};
}
